/* eslint-disable arrow-body-style */
/* eslint-disable-next-line spaced-comment */
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react'

import { AuthType } from 'constants/constants'

import { Grid } from '@mui/material'
import { useAuthContext } from 'AuthProvider'
import { AgGridReact } from 'ag-grid-react'
import clsx from 'clsx'
import { LoadingOverlay } from 'components/commonparts'
import MessageInfo from 'components/commonparts/MessageInfo/MessageInfo'
import { format, isSameDay } from 'date-fns'
import { ja } from 'date-fns/locale'
import {
  useCalcStock,
  useCalcStockParams,
  handleStockCalculation
} from 'hooks/useCalcStock'
import { useCenter } from 'hooks/useCenter'
import { useGridFormatters } from 'hooks/useGridFormatters'
import { useStock, type StockExtended } from 'hooks/useStock'
import _ from 'lodash'
import { AG_GRID_LOCALE } from 'utils/agGridLocale'
import { convertDateToRequestParam } from 'utils/convertDateToRequestParam'
import { isEqualDate, normalizeDate } from 'utils/convertFromLocalDate'

import {
  ROW_TYPE_ARRIVAL,
  ROW_TYPE_QUANTITY_STOP_SHIPPING,
  ROW_TYPE_REMARK,
  ROW_TYPE_SALES_ORDER,
  ROW_TYPE_SHIP,
  ROW_TYPE_SHIP_EXCLUSIVE,
  ROW_TYPE_STOCK,
  ROW_TYPE_STOCK_DAYS,
  ROW_TYPE_STOCK_FOLLOW_BY_PRODUCT,
  ROW_TYPE_STOCK_PRODUCT,
} from '../../../../types/table-model'
import StockCellEditor from '../StockGrid/StockCellEditor'

import type { RenewalSimulatorCell, RenewalSimulatorRow } from '../../../../types/table-model'
import type {
  CellClassParams,
  CellValueChangedEvent,
  EditableCallbackParams,
  ICellEditorParams,
  ICellRendererParams,
  ValueFormatterParams,
  ValueParserParams
} from 'ag-grid-community'
import type { ColDef, ColGroupDef, GridApi, IAggFuncParams, IRowNode } from 'ag-grid-enterprise'
import 'ag-grid-enterprise'
import type {
  GetMstCentersCenterGetRequest,
  GetStockStockGetRequest,
  Daylist,
  RenewalOrgItem,
  StockPlanUpdateBase,
  ResponseResult,
} from 'api-client'

import 'utils/mst.scss'

/** Fix agCheckboxCellRenderer + onCellValueChanged (AG Grid 34 React, issue #12413). */
type OldItemShipStopToggleFn = (
  node: IRowNode<RenewalSimulatorRow>,
  field: string,
  nextBool: boolean,
  baselineCell: RenewalSimulatorCell | undefined,
) => void

type OldItemShipStopFlgCellRendererParams = ICellRendererParams<RenewalSimulatorRow, RenewalSimulatorCell> & {
  editable?: boolean
  onShipStopToggle?: OldItemShipStopToggleFn
}

function OldItemShipStopFlgCellRenderer(params: OldItemShipStopFlgCellRendererParams) {
  const { value: cell, editable, node, colDef, onShipStopToggle } = params
  const checked = Boolean(cell?.value)

  return (
    <span
      className="renewal-ship-stop-checkbox"
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%', height: '100%' }}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={!editable}
        aria-label="old-item-ship-stop"
        onChange={(event) => {
          event.stopPropagation()
          const { target } = event
          if (!editable || !node || !colDef?.field || !onShipStopToggle) return
          const { field } = colDef
          const fieldStr = String(field)
          const baseline = node.data?.[fieldStr as keyof RenewalSimulatorRow] as RenewalSimulatorCell | undefined
          const { checked: next } = target
          if (baseline != null && Boolean(baseline.value) === next) return
          onShipStopToggle(node, fieldStr, next, baseline)
        }}
      />
    </span>
  )
}

function getRenewalSimulatorCellAt(
  node: IRowNode<RenewalSimulatorRow> | undefined,
  field: string | undefined
): RenewalSimulatorCell | null | undefined {
  if (!node?.data || !field) {
    return undefined
  }
  return node.data[field as keyof RenewalSimulatorRow] as RenewalSimulatorCell | null | undefined
}

function getRenewalOrderQuantityRecValue(
  node: IRowNode<RenewalSimulatorRow>,
  field: string | undefined,
  getRowInGroup: (node: IRowNode<RenewalSimulatorRow>, rowTypeCode: string) => IRowNode<RenewalSimulatorRow> | undefined
): number | null | undefined {
  const recCell = getRenewalSimulatorCellAt(getRowInGroup(node, 'orderQuantityRec'), field)
  if (recCell?.value !== undefined && recCell?.value !== null) {
    return recCell.value as number
  }

  const arrivalQuantityCell = getRenewalSimulatorCellAt(getRowInGroup(node, 'arrivalQuantity'), field)
  if (arrivalQuantityCell?.orderQuantityRec != null) {
    return arrivalQuantityCell.orderQuantityRec
  }

  const arrivalCell = getRenewalSimulatorCellAt(getRowInGroup(node, 'arrival'), field)
  if (arrivalCell?.orderQuantityRec != null) {
    return arrivalCell.orderQuantityRec
  }

  return undefined
}

function getRenewalStockQuantityRecValue(
  node: IRowNode<RenewalSimulatorRow>,
  field: string | undefined,
  getRowInGroup: (node: IRowNode<RenewalSimulatorRow>, rowTypeCode: string) => IRowNode<RenewalSimulatorRow> | undefined,
  obj: RenewalSimulatorCell | null | undefined,
  rowTypeCode: string
): number | null | undefined {
  const recCell = getRenewalSimulatorCellAt(getRowInGroup(node, 'stockQuantityRec'), field)
  if (recCell?.value !== undefined && recCell?.value !== null) {
    return recCell.value as number
  }

  if (['stock', 'stock_quantity'].includes(rowTypeCode) && obj?.stockQuantityRec != null) {
    return obj.stockQuantityRec
  }

  return undefined
}

function getRenewalAvgShipValue(
  node: IRowNode<RenewalSimulatorRow>,
  field: string | undefined,
  getRowInGroup: (node: IRowNode<RenewalSimulatorRow>, rowTypeCode: string) => IRowNode<RenewalSimulatorRow> | undefined,
  obj: RenewalSimulatorCell | null | undefined
): number | null | undefined {
  const avgCell = getRenewalSimulatorCellAt(getRowInGroup(node, 'avgShipQuantityStockBase'), field)
  if (avgCell?.value != null && !Number.isNaN(Number(avgCell.value))) {
    return Number(avgCell.value)
  }

  if (obj?.avgShipQuantityStockBase != null && !Number.isNaN(Number(obj.avgShipQuantityStockBase))) {
    return Number(obj.avgShipQuantityStockBase)
  }

  return undefined
}

const SIMULATOR_RECALC_ROW_IDS = [
  '0_arrival',
  '0_ship',
  '0_stock',
  '0_stockDays',
  '0_stockTotal',
  '0_stockTotalDays',
  '1_ship',
  '2_ship',
  '1_stock',
  '2_stock',
] as const

function collectSimulatorRecalcRowNodes(
  gridApi: GridApi<RenewalSimulatorRow>,
  editedNode?: IRowNode<RenewalSimulatorRow> | null
): IRowNode<RenewalSimulatorRow>[] {
  const seen = new Set<string>()
  const nodes: IRowNode<RenewalSimulatorRow>[] = []
  const addNode = (node: IRowNode<RenewalSimulatorRow> | null | undefined) => {
    if (!node?.id || seen.has(node.id)) {
      return
    }
    seen.add(node.id)
    nodes.push(node)
  }

  addNode(editedNode)
  SIMULATOR_RECALC_ROW_IDS.forEach((rowId) => {
    addNode(gridApi.getRowNode(rowId))
  })
  return nodes
}

type SimulatorStyleRefreshBatch = {
  api: GridApi<RenewalSimulatorRow>
  nodeIds: Set<string>
}

let simulatorStyleRefreshBatch: SimulatorStyleRefreshBatch | null = null

/** Re-run cellClass after setDataValue; coalesce multiple edits in the same frame. */
function scheduleRenewalSimulatorCellClassRefresh(
  gridApi: GridApi<RenewalSimulatorRow> | undefined,
  rowNodes: IRowNode<RenewalSimulatorRow>[]
) {
  if (!gridApi) {
    return
  }

  if (!simulatorStyleRefreshBatch || simulatorStyleRefreshBatch.api !== gridApi) {
    simulatorStyleRefreshBatch = { api: gridApi, nodeIds: new Set() }
    queueMicrotask(() => {
      const batch = simulatorStyleRefreshBatch
      simulatorStyleRefreshBatch = null
      if (!batch || batch.api.isDestroyed()) {
        return
      }
      const nodes = Array.from(batch.nodeIds)
        .map((id) => batch.api.getRowNode(id))
        .filter((node): node is IRowNode<RenewalSimulatorRow> => node != null)
      if (nodes.length > 0) {
        batch.api.refreshCells({ rowNodes: nodes, force: true })
      }
    })
  }

  const batch = simulatorStyleRefreshBatch
  rowNodes.forEach((node) => {
    if (node.id) {
      batch.nodeIds.add(node.id)
    }
  })
}

interface ItemRenewalSimulatorProps {
  trCompanyId: any
  centerIdProps: any
  itemCodeProps: any
  onDialogOpen: boolean
  updateAccept: boolean
  setUpdateAccept: (value: boolean) => void
  unitType: 'case' | 'pallet'
  renewalFlgProps: boolean | null
  onDialogClose?: () => void
  onDialogCloseOnly?: () => void
  setResultMessage?: (message: ResponseResult | undefined) => void
}

function ItemRenewalSimulator({
  trCompanyId,
  centerIdProps,
  itemCodeProps,
  onDialogOpen = false,
  updateAccept,
  setUpdateAccept,
  unitType,
  renewalFlgProps,
  onDialogClose,
  onDialogCloseOnly,
  setResultMessage,
}: ItemRenewalSimulatorProps) {
  const { loginUserInfo } = useAuthContext()
  const systemDate = useMemo(() => loginUserInfo?.systemDate, [loginUserInfo])
  const localeText = useMemo<{
    [key: string]: string
  }>(() => AG_GRID_LOCALE, [])

  const gridRef = useRef<AgGridReact<RenewalSimulatorRow>>(null)
  const shipStopToggleRef = useRef<OldItemShipStopToggleFn>(() => {})
  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      suppressHeaderMenuButton: true,
      sortable: false,
      filter: false,
    }),
    []
  )
  const containerStyle = useMemo(() => ({ width: '100%', height: 'calc(100vh - 120px)' }), [])
  const gridStyle = useMemo(() => ({ width: '100%', height: 'calc(100vh - 150px)' }), [])
  const [daylist, setDaylist] = useState<Daylist[]>([])
  const [rowData, setRowData] = useState<RenewalSimulatorRow[]>([])
  const hasRunRef = useRef(false)
  const hasFetch = useRef(false)

  const [errorMessage, setErrorMessage] = useState<ResponseResult | undefined>(undefined)
  const { stockData, fetchStockData, isLoading, fetchUpdateStock, isUpdating } = useStock()
  const calcStockHook = useCalcStock()
  const gridCalc = useMemo(() => calcStockHook.getGridCalculators(), [calcStockHook])
  const { centerSearch, fetchCenterSearch } = useCenter()
  const {
    isNonInputValue,
    parsePositiveInteger,
    longTextValueParser,
    numberValueFormatter,
    longTextValueFormatter,
  } = useGridFormatters()

  const calcParams = useCalcStockParams(systemDate, centerIdProps, trCompanyId, centerSearch)

  const simulatorFetching = () => {
    const requestParam: GetStockStockGetRequest = {
      companyId: trCompanyId,
      centerIds: [centerIdProps],
      itemCode: itemCodeProps,
      viewUnitType: unitType,
      renewal: renewalFlgProps,
    }
    fetchStockData(requestParam)
  }
  useEffect(() => {
    if (onDialogOpen && !hasFetch.current) {
      simulatorFetching()
      // Fetch centerSearch để lấy calcStockShippingorderFlg
      if (trCompanyId) {
        const centerSearchParam: GetMstCentersCenterGetRequest = {
          companyId: trCompanyId,
        }
        fetchCenterSearch(centerSearchParam).catch(() => {
          // Handle error silently
        })
      }
      hasFetch.current = true
    }
  }, [onDialogOpen, hasFetch, trCompanyId, fetchCenterSearch])

  const HEADER_GROUP_REC = 'headerGroupRec'
  const HEADER_GROUP_TODAY = 'headerGroupToday'
  const HEADER_GROUP_EXPECTED = 'headerGroupExpected'

  /**
   * 日付リストのすべての日付カラム名（col1, col2,...）を返す
   */
  const allDayHeaders: string[] = useMemo(() => {
    if (!daylist) return []

    const headerList = daylist
      .sort((a: Daylist, b: Daylist) => (a.dateno || 0) - (b.dateno || 0))
      .map((day) => day.colname)
    return headerList.filter((header): header is string => header !== null && header !== undefined)
  }, [daylist])

  const textSort = (a: string, b: string) => {
    if (a === 'センター合計') {
      return -1
    }
    if (b === 'センター合計') {
      return 1
    }
    // カスタムソートロジック
    if (a < b) {
      return -1
    }
    if (a > b) {
      return 1
    }
    return 0
  }

  /**
   * 指定された日付カラムを含む、全ての未来日付のカラム名を返す
   * @param col 日付カラム名
   * @returns
   */
  function allNextCols(col: string): string[] {
    if (!allDayHeaders) return []
    const index = allDayHeaders.indexOf(col)
    if (index !== -1) {
      return allDayHeaders.slice(index)
    }

    return []
  }

  /**
   * 指定された日付カラムの1日前のカラム名を返す
   *
   * @param col 日付カラム名
   * @returns
   */
  function beforeCol(col: string) {
    const index = parseInt(col.replace('col', ''), 10)
    return `col${index - 1}`
  }

  function afterCol(col: string) {
    const index = parseInt(col.replace('col', ''), 10)
    return `col${index + 1}`
  }

  /**
   * 指定された行が属する商品の全ての行（入荷数、出荷数、在庫数、在庫日数）のなかから、rowTypeCodeで指定された種別の行を返す
   *
   * @param rowNode
   * @param rowTypeCode
   * @returns
   */
  const getRowInGroup = useCallback((
    rowNode: IRowNode<RenewalSimulatorRow>,
    rowTypeCode: string
  ): IRowNode<RenewalSimulatorRow> | undefined => {
    let groupNode = rowNode.parent
    if (groupNode?.parent?.allLeafChildren) {
      groupNode = groupNode.parent
    }
    const allLeaves = groupNode?.allLeafChildren
    if (!allLeaves) return undefined
    return allLeaves.find((row) => row?.data?.rowType?.code === rowTypeCode)
  }, [])

  /**
   * 指定されたセルの在庫数を再計算する
   * @param rowNode 行データ
   * @param field カラム名
   * @returns
   */
  function calcStock(rowNode: IRowNode<RenewalSimulatorRow>, field: string) {
    
    const trColData = rowNode.data?.[field as keyof RenewalSimulatorRow] as RenewalSimulatorCell
    const stockCell =
    (getRowInGroup(rowNode, 'stock')?.data?.[field as keyof RenewalSimulatorRow] as RenewalSimulatorCell) || {}

    // 通常の在庫計算以外のイレギュラー処理

    // 初日の在庫数はデフォルトのまま計算しない
    if ((field === firstColName()) || (rowNode?.data?.renewalNumber === 0 && trColData?.recType === 'rec')){
      return {
        ...stockCell,
        value: stockCell.value,
      }
    }
    // 旧商品のイレギュラー
    if (rowNode.data?.renewalNumber === 1) {
      // 出荷停止フラグがONの場合、在庫数は0
      const stopShipRow = getRowInGroup(rowNode, 'olditemShipStopFlg')
      const stopFlgCell = stopShipRow?.data?.[field as keyof RenewalSimulatorRow] as RenewalSimulatorCell
      if (stopFlgCell?.value === true) {
        return {
          ...stockCell,
          value: 0,
        }
      }
    }

    // 前日の在庫数を取得（負の場合は0とする）
    const beforeRenewalSimulatorRow = getRowInGroup(rowNode, 'stock')
    const beforeCell = beforeRenewalSimulatorRow?.data?.[
      beforeCol(field) as keyof RenewalSimulatorRow
    ] as RenewalSimulatorCell
    const beforeStock = Math.max(beforeCell?.value || 0, 0)

    // 当日の入荷数を取得
    const arrivalRow = getRowInGroup(rowNode, 'arrival')
    const arrivalCell = arrivalRow?.data?.[field as keyof RenewalSimulatorRow] as RenewalSimulatorCell
    const arrival = arrivalCell?.value ?? 0

    // 当日の出荷数を取得
    const shipRow = getRowInGroup(rowNode, 'ship')
    const shipCell = shipRow?.data?.[field as keyof RenewalSimulatorRow] as RenewalSimulatorCell
    const ship = shipCell?.value ?? 0
    
    const salesOrderRow = getRowInGroup(rowNode, 'salesOrder')
    const salesOrderCell = salesOrderRow?.data?.[field as keyof RenewalSimulatorRow] as RenewalSimulatorCell
    const salesOrder = salesOrderCell?.value ?? 0

    const shipStopRec = stockData?.list?.find(
      (s: StockExtended) =>
        s.recDate &&
        shipCell?.recDate &&
        s.centerId === centerIdProps &&
        s.itemCode === itemCodeProps &&
        isSameDay(s.recDate, shipCell.recDate)
    ) as StockExtended | undefined
    const shipStop = Number(shipStopRec?.stockQuantityShipStop ?? 0)

    const rawStock = gridCalc.calcCellStock(
      beforeStock,
      arrival,
      ship,
      salesOrder,
      shipCell?.recDate,
      calcParams,
      shipStop
    )

    const finalStock = rowNode.data?.renewalNumber === 1 ? Math.max(0, rawStock) : rawStock

    return {
      ...stockCell,
      value: finalStock,
    }
  }

  function findRowByTypeAndItem(api: any, rowId: string): IRowNode<RenewalSimulatorRow> | undefined {
    let foundRow: IRowNode<RenewalSimulatorRow> | undefined
    api.forEachNode((node: IRowNode<RenewalSimulatorRow>) => {
      if (node.data && node.data.rowid === rowId) {
        foundRow = node
      }
    })
    return foundRow
  }
  // Calculate total stock with formular = stockRenewalNewItemNode + stockRenewalOldItemNode + quantityStopNode
  function calcTotalStockForItem(api: any, field: string) {
    const stockRenewalOldItemNode = findRowByTypeAndItem(api, '1_stock')
    const stockRenewalNewItemNode = findRowByTypeAndItem(api, '2_stock')
    const quantityStopNode = findRowByTypeAndItem(api, '1_quantityStopShipping')

    const stockRenewalNewItem =
      (stockRenewalNewItemNode?.data?.[field as keyof RenewalSimulatorRow] as RenewalSimulatorCell)?.value ?? 0
    const stockRenewalOldItem =
      (stockRenewalOldItemNode?.data?.[field as keyof RenewalSimulatorRow] as RenewalSimulatorCell)?.value ?? 0
    const quantityStop =
      (quantityStopNode?.data?.[field as keyof RenewalSimulatorRow] as RenewalSimulatorCell)?.value ?? 0

    const total = Number(stockRenewalNewItem) + Number(stockRenewalOldItem) + Number(quantityStop)
    return total
  }

  // Set ship quantity for newItem and oldItem
  function setNewOrgItemShip(api: any, field: string, rowid: string) {
    let value
    const col = beforeCol(field)
    // find row to get data
    const initalShipNode = findRowByTypeAndItem(api, '0_ship')
    const oldItemArrivalNode = findRowByTypeAndItem(api, '1_arrival')
    const oldItemShipNode = findRowByTypeAndItem(api, '1_ship')
    const olditemShipStopFlgNode = findRowByTypeAndItem(api, '1_olditemShipStopFlg')
    const beforeStockNode = findRowByTypeAndItem(api, '1_stock')

    // get value from row
    const initalShip = (initalShipNode?.data?.[field as keyof RenewalSimulatorRow] as RenewalSimulatorCell)?.value || 0
    const oldItemArrival = (oldItemArrivalNode?.data?.[field as keyof RenewalSimulatorRow] as RenewalSimulatorCell)?.value || 0
    const oldItemShip = (oldItemShipNode?.data?.[field as keyof RenewalSimulatorRow] as RenewalSimulatorCell)?.value || 0
    const olditemShipStopFlg = (olditemShipStopFlgNode?.data?.[field as keyof RenewalSimulatorRow] as RenewalSimulatorCell)?.value || false

    const beforeStock =
      col === 'col0'
        ? initalShip
        : (beforeStockNode?.data?.[col as keyof RenewalSimulatorRow] as RenewalSimulatorCell)?.value || 0

    if (rowid === '1_ship') {
      if (olditemShipStopFlg) {
        value = 0
      } else {
        value = Number(beforeStock) + Number(oldItemArrival) > Number(initalShip) ? Number(initalShip) : Number(beforeStock) + Number(oldItemArrival)
      }
    }

    if (rowid === '2_ship') {
      value = Number(initalShip) - Number(oldItemShip)
    }

    return value
  }

  // Calculate quantityStopShipping
  function calcQuantityStopShipping(api: any, field: string) {
    const olditemShipStopFlgNode = findRowByTypeAndItem(api, '1_olditemShipStopFlg')
    const stockQuantityNode = findRowByTypeAndItem(api, '1_stock')

    const stockQuantity =
      (stockQuantityNode?.data?.[field as keyof RenewalSimulatorRow] as RenewalSimulatorCell)?.value || 0
    const olditemShipStopFlg =
      (olditemShipStopFlgNode?.data?.[afterCol(field) as keyof RenewalSimulatorRow] as RenewalSimulatorCell)?.value || false

    return olditemShipStopFlg ? stockQuantity * -1 : 0
  }

  /**
   * 指定されたセルの在庫日数を再計算する
   * @param rowNode 行データ
   * @param field カラム名
   * @returns
   */
  function calcStockDays(rowNode: IRowNode<RenewalSimulatorRow>, field: string) {
    // 当日の在庫数を取得
    const RenewalSimulatorRow = getRowInGroup(rowNode, 'stock')
    const stockCell = RenewalSimulatorRow?.data?.[field as keyof RenewalSimulatorRow] as RenewalSimulatorCell
    const stock = stockCell?.value ?? 0

    // 同じグループの出荷行を取得
    const shipRow = getRowInGroup(rowNode, 'ship')
    const salesOrderRow = getRowInGroup(rowNode, 'salesOrder')

    // 当日の在庫日数（自分自身）を取得
    const stockDaysRow = getRowInGroup(rowNode, 'stockDays')
    const stockDaysCell = stockDaysRow?.data?.[field as keyof RenewalSimulatorRow] as RenewalSimulatorCell

    const blankData = {
      ...stockDaysCell,
      value: null,
    }

    // 当日を含めて7日以上のデータが無い（未来データが無い）場合は無効とする
    const futureColsWithToday = allNextCols(field)
    if (futureColsWithToday.length <= 7) {
      return blankData
    }

    const futureCols = futureColsWithToday.slice(1, 8)
    const futureStocks = futureCols
      .map((col) => {
        const shipCell = shipRow?.data?.[col as keyof RenewalSimulatorRow] as RenewalSimulatorCell
        const salesOrderCell = salesOrderRow?.data?.[col as keyof RenewalSimulatorRow] as RenewalSimulatorCell
        
        return {
          shipQuantity: shipCell?.value ?? 0,
          salesorderQuantity: salesOrderCell?.value ?? 0,
          enableShip: shipCell?.enableData ?? false,
          recDate: shipCell?.recDate,
        }
      })
      .filter(item => item.enableShip)

    const calculatedStockDays = gridCalc.calcCellStockDays(
      stock,
      futureStocks,
      calcParams
    )

    return {
      ...stockDaysCell,
      value: calculatedStockDays,
    }
  }

  /**
   * Calculate total stock days with total 商品別在庫数合計 / (total / 7)
   * @param rowNode 行データ
   * @param field カラム名
   * @returns
   */
  function calcTotalStockDays(rowNode: IRowNode<RenewalSimulatorRow>, field: string) {
    // 当日の在庫数を取得
    const RenewalSimulatorRow = getRowInGroup(rowNode, 'stockTotal')
    const stockCell = RenewalSimulatorRow?.data?.[field as keyof RenewalSimulatorRow] as RenewalSimulatorCell
    const stock = stockCell?.value ?? 0

    // 同じグループの出荷行を取得
    const shipRow = getRowInGroup(rowNode, 'ship')
    const salesOrderRow = getRowInGroup(rowNode, 'salesOrder')

    // 当日の在庫日数（自分自身）を取得
    const stockDaysRow = getRowInGroup(rowNode, 'stockTotalDays')
    const stockDaysCell = stockDaysRow?.data?.[field as keyof RenewalSimulatorRow] as RenewalSimulatorCell

    const blankData = {
      ...stockDaysCell,
      value: null,
    }

    // 当日を含めて7日以上のデータが無い（未来データが無い）場合は無効とする
    const futureColsWithToday = allNextCols(field)
    if (futureColsWithToday.length <= 7) {
      return blankData
    }

    const futureCols = futureColsWithToday.slice(1, 8)
    const futureStocks = futureCols
      .map((col) => {
        const shipCell = shipRow?.data?.[col as keyof RenewalSimulatorRow] as RenewalSimulatorCell
        const salesOrderCell = salesOrderRow?.data?.[col as keyof RenewalSimulatorRow] as RenewalSimulatorCell
        
        return {
          shipQuantity: shipCell?.value ?? 0,
          salesorderQuantity: salesOrderCell?.value ?? 0,
          enableShip: shipCell?.enableData ?? false,
          recDate: shipCell?.recDate,
        }
      })
      .filter(item => item.enableShip)

    const calculatedStockDays = gridCalc.calcCellStockDays(
      stock,
      futureStocks,
      calcParams
    )
    
    // Apply non-negative constraint for total stock days
    const stockDays = calculatedStockDays !== null ? Math.max(0, calculatedStockDays) : null
    
    return {
      ...stockDaysCell,
      value: stockDays,
    }
  }

  const calcAllAfterDays = (calcStartColName:string) => {
    // 開始カラムに指定された日付以降の全ての日付について、シミュレーション計算を実行する

    const gridApi = gridRef.current?.api
    if (!gridApi) return

    allNextCols(calcStartColName).forEach((col) => {

      // １日分の計算
      const isValidColumn = (colId: string) => gridApi.getColumnDef(colId) !== null
      if (col && isValidColumn(col)) {
        const arrivalNode = gridApi.getRowNode('0_arrival')
        const arrivalCell = arrivalNode?.data?.[col as keyof RenewalSimulatorRow] as RenewalSimulatorCell
        
        const arrivalOldNode = gridApi.getRowNode('1_arrival')
        const arrivalOldCell = arrivalOldNode?.data?.[col as keyof RenewalSimulatorRow] as RenewalSimulatorCell
        if (arrivalCell?.renewalNumberDay === arrivalOldCell?.renewalNumberDay && arrivalCell) {
            const newCell = arrivalOldCell ? { ...arrivalOldCell, value: arrivalCell.value } : { value: arrivalCell.value }
            arrivalOldNode?.setDataValue(col, newCell)
        }

        const arrivalNewNode = gridApi.getRowNode('2_arrival')
        const arrivalNewCell = arrivalNewNode?.data?.[col as keyof RenewalSimulatorRow] as RenewalSimulatorCell
        if (arrivalCell?.renewalNumberDay === arrivalNewCell?.renewalNumberDay && arrivalCell) {
            const newCell = arrivalNewCell ? { ...arrivalNewCell, value: arrivalCell.value } : { value: arrivalCell.value }
            arrivalNewNode?.setDataValue(col, newCell)
        }

        const shipNode = gridApi.getRowNode('0_ship');
        const shipCell = shipNode?.data?.[col as keyof RenewalSimulatorRow] as RenewalSimulatorCell

        const shipOldNode = gridApi.getRowNode('1_ship');
        const shipOldCell = shipOldNode?.data?.[col as keyof RenewalSimulatorRow] as RenewalSimulatorCell

        if (shipOldNode) {
          const newTotal = setNewOrgItemShip(gridApi, col, '1_ship')
          const newCell = shipOldCell ? { ...shipOldCell, value: newTotal } : { shipCell: newTotal }
          shipOldNode?.setDataValue(col, newCell)
        }

        const shipNewNode = gridApi.getRowNode('2_ship');
        const shipNewCell = shipNewNode?.data?.[col as keyof RenewalSimulatorRow] as RenewalSimulatorCell

        if (shipCell?.renewalNumberDay === 2) {
          const newTotal = setNewOrgItemShip(gridApi, col, '2_ship')
          const newCell = shipNewCell ? { ...shipNewCell, value: newTotal } : { shipCell: newTotal }
          shipNewNode?.setDataValue(col, newCell)
        } else{
          shipNewNode?.setDataValue(col, { ...shipNewCell, value: 0 })
        }

        let node = gridApi.getRowNode('0_stock')
        if (node) node.setDataValue(col, calcStock(node, col))

        // 在庫日数の計算は在庫数に依存するため、在庫数の計算が完了してから行う
        node = gridApi.getRowNode('0_stockDays')
        if (node) node.setDataValue(col, calcStockDays(node, col))

        node = gridApi.getRowNode('1_quantityStopShipping')
        if (node) {
          const newTotal = calcQuantityStopShipping(gridApi, col)
          const currentCell = (node.data as RenewalSimulatorRow)[col as keyof RenewalSimulatorRow] as
            | RenewalSimulatorCell
            | undefined
          const newCell = currentCell ? { ...currentCell, value: newTotal } : { value: newTotal }
          node.setDataValue(col, newCell)
        }
        node = gridApi.getRowNode('1_stock')
        if (node) node.setDataValue(col, calcStock(node, col))
        node = gridApi.getRowNode('1_stockDays')
        if (node) node.setDataValue(col, calcStockDays(node, col))
            
        node = gridApi.getRowNode('2_stock')
        if (node) node.setDataValue(col, calcStock(node, col))
        node = gridApi.getRowNode('2_stockDays')
        if (node) node.setDataValue(col, calcStockDays(node, col))

        node = gridApi.getRowNode('0_stockTotal')
        if (node) {
          const newTotal = calcTotalStockForItem(gridApi, col)
          const currentCell = (node.data as RenewalSimulatorRow)[col as keyof RenewalSimulatorRow] as
            | RenewalSimulatorCell
            | undefined
          const newCell = currentCell ? { ...currentCell, value: newTotal } : { value: newTotal }

          node.setDataValue(col, newCell)
        }

        node = gridApi.getRowNode('0_stockTotalDays')
        if (node) {
          node.setDataValue(col, calcTotalStockDays(node, col))
        }
      }
    })
  }

  const calcAllAfterDaysRef = useRef(calcAllAfterDays)
  calcAllAfterDaysRef.current = calcAllAfterDays

  const firstColName = useCallback((): string => allDayHeaders[0] || 'col1', [allDayHeaders])

  /** Full dependent-row recalc from edited column + refresh cell styles (single pass). */
  const commitSimulatorRecalcFromColumn = useCallback(
    (calcStartColName: string, editedNode?: IRowNode<RenewalSimulatorRow> | null) => {
      const api = gridRef.current?.api
      if (!api) {
        return
      }
      calcAllAfterDaysRef.current(calcStartColName)
      scheduleRenewalSimulatorCellClassRefresh(
        api,
        collectSimulatorRecalcRowNodes(api, editedNode)
      )
    },
    []
  )

  const runInitialSimulatorGridCalc = useCallback(() => {
    commitSimulatorRecalcFromColumn(firstColName())
  }, [commitSimulatorRecalcFromColumn, firstColName])

  /**
   * セルが編集可能かどうかを判定する
   */
  const isCellEditable = useCallback(
    (colEditable: boolean, rowTypeCode: string | undefined, obj: RenewalSimulatorCell | null | undefined): boolean => {
      if (!loginUserInfo || loginUserInfo.authType === AuthType.ReadOnly || loginUserInfo?.companyId !== trCompanyId ) {
        return false;
      }
      if (!rowTypeCode || unitType === 'pallet') {
        return false
      }
      if (rowTypeCode === ROW_TYPE_REMARK.code || rowTypeCode === ROW_TYPE_SHIP_EXCLUSIVE.code) {
        return true
      }

      const rowEditable = ['arrival', 'ship', 'olditemShipStopFlg', 'remark'].includes(rowTypeCode)
      const cellEditable = obj?.enableUpdate || false
      return rowEditable && colEditable && cellEditable
    },
    [unitType]
  )

  /**
   * セルの表示フォーマットを設定する
   */
  const valueFormatter = useCallback(
    (params: ValueFormatterParams<RenewalSimulatorRow, RenewalSimulatorCell>) => {
      const { data, value: obj } = params;
      const rowTypeCode = data?.rowType?.code;
      const rowid = data?.rowid;

      if (!obj || isNonInputValue(obj)) return '';

      if (unitType === 'case' && ['arrival', 'ship', 'stock'].includes(rowTypeCode || '')) {
        if (isNonInputValue(obj.value)) return '-';
        return numberValueFormatter({ ...params, value: Math.round(Number(obj.value)) });
      }

      if (rowid === '0_remark') {
        return longTextValueFormatter({ ...params, value: obj.value });
      }

      if (rowTypeCode === 'stockDays' || rowTypeCode === 'stockTotalDays') {
        if (isNonInputValue(obj.value)) return '-';
        const val = Number(obj.value);
        if (Number.isNaN(obj.value)) return '-';
        if (val <= 0) return '0';
        if (val > 999) return '999 +';
        return val.toFixed(1);
      }

      if (isNonInputValue(obj.value)) return '';
      return String(obj.value);
    },
    [unitType]
  )

  /**
   * セルのクラスを設定する
   * @param colEditable 編集可能列かどうか
   * @param rectype 日付種別（rec, today, expected）
   * @param obj セル情報
   * @param rowTypeCode 行種別（arrival, ship, stock, stockDays）
   *
   */
  const cellClass = useCallback(
    ({
      colEditable,
      rectype,
      obj,
      rowTypeCode,
      field,
      node,
    }: {
      colEditable: boolean
      rectype: string
      obj: RenewalSimulatorCell | null | undefined
      rowTypeCode: string
      field: string | undefined
      node: IRowNode<RenewalSimulatorRow>
    }) => {
      const classes = ['number-cell']
      const resolvedRowTypeCode = rowTypeCode || ''

      if (obj?.isOrderConfirmed && rectype === 'expected') {
        classes.push('order-confirmed')
      }

      if (['arrival', 'arrivalQuantity'].includes(resolvedRowTypeCode)) {
        if (
          obj?.orderDate &&
          systemDate &&
          isEqualDate(obj.orderDate, systemDate) &&
          obj?.isTodayOrderDay &&
          obj?.isArrivalDay
        ) {
          classes.push('todays-order-cell')
        }
      }

      if (['stock', 'stock_quantity'].includes(resolvedRowTypeCode)) {
        const avgShipValue = getRenewalAvgShipValue(node, field, getRowInGroup, obj)
        const stockQuantityRecValue = getRenewalStockQuantityRecValue(
          node,
          field,
          getRowInGroup,
          obj,
          resolvedRowTypeCode
        )
        const minStockQuantity = obj?.minStockQuantity

        if (
          ['today', 'expected'].includes(rectype) &&
          obj?.value != null &&
          obj.value <= 0 &&
          avgShipValue != null &&
          avgShipValue > 0
        ) {
          classes.push('alert_hight')
        }

        if (['today', 'expected'].includes(rectype) &&
            avgShipValue != null &&
            avgShipValue > 0 &&
            minStockQuantity != null &&
            obj?.value != null &&
            obj.value < minStockQuantity)
          {
          classes.push('alert_middle')
        }

        if (stockQuantityRecValue == null && ['rec'].includes(rectype)) {
          classes.push('alert_low')
        }
      }

      if (resolvedRowTypeCode === 'stockTotal') {
        if (['today', 'expected'].includes(rectype) && obj?.value != null && obj.value <= 0) {
          classes.push('alert_middle')
        }
      }

      if (resolvedRowTypeCode === 'stockDays') {
        const avgShipValue = getRenewalAvgShipValue(node, field, getRowInGroup, obj)
        const shipdaysNextArrival = obj?.shipdaysNextArrival
        const stockDaysSetting = obj?.stockDaysSetting ?? node.data?.stockDaysSetting ?? null

        if (
          ['today', 'expected'].includes(rectype) &&
          obj?.value != null &&
          shipdaysNextArrival != null &&
          obj.value <= shipdaysNextArrival
        ) {
          classes.push('alert_hight')
        }
        if (
          ['today', 'expected'].includes(rectype) &&
          avgShipValue != null &&
          avgShipValue > 0 &&
          stockDaysSetting != null &&
          obj?.value != null &&
          obj.value <= stockDaysSetting
        ) {
          classes.push('alert_middle')
        }
      }

      if (['ship', 'shipQuantity'].includes(resolvedRowTypeCode) && field) {
        const stockRow = getRowInGroup(node, 'stock_quantity') ?? getRowInGroup(node, 'stock')
        if (stockRow) {
          if (obj?.value != null && obj.value > 0 && ['today', 'expected'].includes(rectype)) {
            const prevStockCell = getRenewalSimulatorCellAt(stockRow, beforeCol(field))
            if (prevStockCell?.value != null && obj.value >= prevStockCell.value && obj.value > 0) {
              classes.push('alert_hight')
            }
          }
          if (obj?.value != null && obj.value > 0 && ['expected'].includes(rectype)) {
            const salesOrderRow = getRowInGroup(node, 'salesorderQuantity') ?? getRowInGroup(node, 'salesOrder')
            const salesOrderCell = getRenewalSimulatorCellAt(salesOrderRow, field)
            const orderQuantityRecValue = getRenewalOrderQuantityRecValue(node, field, getRowInGroup)

            if (salesOrderCell?.value != null && orderQuantityRecValue == null) {
              classes.push('alert_low')
            }
          }
        }
      }

      if (['salesOrder', 'salesorderQuantity'].includes(resolvedRowTypeCode) && field) {
        const orderQuantityRecValue = getRenewalOrderQuantityRecValue(node, field, getRowInGroup)
        if (['expected'].includes(rectype) && obj?.value != null && orderQuantityRecValue == null) {
          classes.push('alert_low')
        }
      }

      if (isCellEditable(colEditable, resolvedRowTypeCode, obj)) {
        classes.push('editable-cell')
      }

      if (obj?.isCurrentUpdate) {
        classes.push('current-updated-cell')
      }

      if (obj?.isUpdate) {
        classes.push('updated-cell')
      }

      if (rectype === 'today' && (!node.group || (node.level ?? 0) >= 3)) {
        classes.push('todays-order-cell')
      }

      if (['arrival', 'arrivalQuantity'].includes(resolvedRowTypeCode)) {
        const hasOrderQuantityRec =
          obj?.orderQuantityRec !== null && obj?.orderQuantityRec !== undefined
        const hasOrderQuantityRecommend =
          obj?.orderQuantityRecommend !== null &&
          obj?.orderQuantityRecommend !== undefined &&
          (obj?.orderQuantityRecommend ?? 0) > 0
        const arrivalFixFlg = obj?.fixFlg

        if (
          ['today', 'expected'].includes(rectype) &&
          !obj?.isUpdatedArrivalTmp &&
          hasOrderQuantityRecommend &&
          !hasOrderQuantityRec &&
          !arrivalFixFlg
        ) {
          classes.push('order-recommend')
        }
        if (rectype === 'expected' && hasOrderQuantityRec) {
          classes.push('future-arrival-exists')
        }
      }

      return classes
    },
    [systemDate, getRowInGroup, beforeCol, isCellEditable]
  )

  /**
   * セルの値を変更した際の処理
   * セル情報をオブジェクトとして保持しているため定義が必要
   */
  const valueParser = useCallback((params: ValueParserParams) => {
    const { newValue, oldValue, data, colDef } = params;

    let updatedValue = newValue;
    const { isCurrentUpdate, value: oldVal } = oldValue || {};
    const isUpdateData = (isCurrentUpdate === true) || (updatedValue !== oldVal);

    if (isNonInputValue(updatedValue)) {
      updatedValue = ''
    }

    if (data.rowid === '0_ship' || data.rowid === '0_arrival') {
      const parsed = parsePositiveInteger(updatedValue);
      updatedValue = parsed !== null ? String(parsed) : '';
    }

    if (data.rowid === '0_remark' && typeof updatedValue === 'string') {
      const parsed = longTextValueParser({ ...params, newValue: updatedValue });
      updatedValue = parsed !== null ? parsed : '';
    }

    return {
      ...oldValue,
      value: updatedValue,
      isCurrentUpdate: isUpdateData,
    };
  }, []);


  /**
   * 日付カラムの表示名を取得する
   * @param day
   * @returns
   */
  function getheaderName(day: Daylist) {
    return day.datevalue ? format(day.datevalue, 'M/d(E)', { locale: ja }) : ''
  }

  /**
   * 日付カラムのカラム定義を生成する
   * @param rectype 日付種別（rec, today, expected）
   * @param colEditable 編集可能列かどうか
   * @returns
   */
  const genDayCols = (rectype: string, colEditable: boolean) => {
    if (!daylist) return []

    return daylist
      .filter((day: Daylist) => (day.rectype === rectype && day.renewalNumber !== 0))
      .sort((a: Daylist, b: Daylist) => (a.dateno || 0) - (b.dateno || 0))
      .map((day: Daylist, idx: number) => ({
        headerName: getheaderName(day),
        field: day.colname ? day.colname : undefined,
        width: 110,
        type: 'rightAligned',
        headerClass: (params: any) => {
          const classes = clsx(`value-header-${rectype}`, {
            sunday: day.datevalue?.getDay() === 0,
            newitem_start_header: day.newitemStartFlg,
          })
          return classes
        },
        editable: (params: EditableCallbackParams<RenewalSimulatorRow>) => {
          if (params.data?.rowType?.code === 'olditemShipStopFlg') {
            return false
          }
          const obj = params.data?.[params.colDef.field as keyof RenewalSimulatorRow] as RenewalSimulatorCell
          return isCellEditable(colEditable, params.data?.rowType?.code, obj)
        },
        cellRendererSelector: (params: ICellRendererParams<RenewalSimulatorRow>) => {
          if (params.data?.rowType?.code === 'olditemShipStopFlg') {
            const editable = isCellEditable(colEditable, params.data?.rowType?.code, params.value)
            return {
              component: OldItemShipStopFlgCellRenderer,
              params: {
                editable,
                onShipStopToggle: (
                  node: IRowNode<RenewalSimulatorRow>,
                  field: string,
                  next: boolean,
                  baseline: RenewalSimulatorCell | undefined
                ) => shipStopToggleRef.current(node, field, next, baseline),
              },
            }
          }
          return undefined
        },
        valueFormatter,
        cellClass: (params: CellClassParams<RenewalSimulatorRow>) =>
          cellClass({
            colEditable,
            rectype,
            obj: params.value,
            rowTypeCode: params.data?.rowType?.code || '',
            field: params.colDef.field,
            node: params.node,
          }),
        valueParser,
        cellEditorSelector: (params: ICellEditorParams<RenewalSimulatorRow>) => {
          switch (params.data?.rowType?.code) {
            case ROW_TYPE_REMARK.code: {
              let raw = params.value?.value ?? '';
              if (raw.startsWith('"') && raw.endsWith('"') && /[\r\n]/.test(raw)) {
                raw = raw.slice(1, -1);
              }
              return {
                component: 'agLargeTextCellEditor',
                popup: true,
                params: {
                  maxLength: 1000,
                  value: raw,
                },
              }
            }
            case 'arrival':
            case 'ship':
              return {
                component: StockCellEditor,
              }
            default:
              return {
                component: StockCellEditor,
              }
          }
        },
      }))
  }

  /**
   * カラム定義
   * @returns
   */
  const updateColumnDefs = (): (ColDef | ColGroupDef)[] => [
    {
      headerName: '',
      width: 320,
      aggFunc: (params: IAggFuncParams<RenewalSimulatorRow>) => params.rowNode?.allLeafChildren?.[0]?.data?.itemName,
      valueFormatter: (params) => {
        // Only show text for the first row of the group, the rest are empty
        if (params.node?.group) {
          return params.value;
        }
        return '';
      },
      field: 'itemName',
      pinned: true,
      suppressHeaderMenuButton: false,
      menuTabs: ['filterMenuTab'],
      cellRendererParams: {
        companyId: null,
        disableLink: true,
      },
    },
    {
      headerName: '',
      pinned: true,
      showRowGroup: 'itemCode',
      field: 'rowType.name',
      cellRenderer: (params: any) => {
        if (params.data?.rowid === '0_remark') {
          return params.data?.rowType?.name || '';
        }
        return params.value;
      },
    },
    {
      field: 'itemCode',
      rowGroup: true,
      filter: 'agTextColumnFilter',
      menuTabs: ['filterMenuTab'],
      hide: true,
      valueGetter: (params) => {
        const rowid = params.data?.rowid;
        if (rowid && (
          rowid === '0_arrival' ||
          rowid === '0_ship' ||
          rowid === '0_stock' ||
          rowid === '0_stockDays' ||
          rowid === '0_salesOrder' ||
          rowid === '0_stockTotal' ||
          rowid === '0_stockTotalDays'
        )) {
          return '実績ベース';
        }
        return params.data?.itemCode || '';
      },
    },
    {
      groupId: HEADER_GROUP_REC,
      headerName: '実績',
      headerClass: 'value-header-rec',
      children: genDayCols('rec', false),
    },
    {
      groupId: HEADER_GROUP_TODAY,
      headerName: '当日',
      headerClass: 'value-header-today',
      children: genDayCols('today', true),
    },
    {
      groupId: HEADER_GROUP_EXPECTED,
      headerName: '予定',
      headerClass: 'value-header-expected',
      children: genDayCols('expected', true),
    },
  ]

  const columnDefs = useMemo<(ColDef | ColGroupDef)[]>(updateColumnDefs, [daylist])

  // Define row class using params
  const getRowClass = (params: any) => {
    const classes: string[] = []
    if (params.node?.level === 0) {
      classes.push('group-level-0')
    }
    if (!params.data) {
      return classes.join(' ').trim() || ''
    }

    const { rowType } = params.data

    // const boldRowTypes = [
    //   ROW_TYPE_ARRIVAL,
    //   ROW_TYPE_SHIP,
    //   ROW_TYPE_SALES_ORDER,
    //   ROW_TYPE_STOCK,
    //   ROW_TYPE_STOCK_DAYS,
    //   ROW_TYPE_QUANTITY_STOP_SHIPPING,
    //   ROW_TYPE_SHIP_EXCLUSIVE,
    // ]
    // if (boldRowTypes.includes(rowType)) {
    //   classes.push('font-style')
    // }

    if (rowType === ROW_TYPE_STOCK_FOLLOW_BY_PRODUCT || rowType === ROW_TYPE_STOCK_PRODUCT) {
      classes.push('bold-border-cell')
    }
    return classes.join(' ').trim() || ''
  }

  /**
   * Grid上で扱いやすくするため、Stockデータの入荷数・出荷数・在庫数・在庫日数をそれぞれ行に分割する
   *
   * @param dataList 商品リスト（StockExtended）
   * @param dayList  日付リスト（Daylist）
   * @returns 入荷数・出荷数・在庫数・在庫日数をそれぞれ行に分割したデータ
   */
  const resultToRows = (
    dataList: Array<StockExtended>,
    dayList: Array<Daylist>,
    renewalList: Array<RenewalOrgItem>
  ): RenewalSimulatorRow[] => {
    const dataByGroup = _.groupBy(dataList, 'itemCode')
    const rows: RenewalSimulatorRow[] = []
    /*
     * データ行作成
     */
    _.forEach(dataByGroup, (stocks, key) => {
      const today = stocks.find((stock) => stock.rectype === 'today')
      const {
        companyId,
        centerId,
        orgItemCode,
        itemCode,
        itemName,
        janCase,
        expectedRank,
        orderUnitTypeName,
        caseLot,
        minOrderQuantity,
        stockDaysSetting,
        safetyStockSetting,
        minStockQuantity,
        startDate,
        stopShip,
        stopArrival,
        renewalFlg,
        renewalNumber,
        weightCalc,
        optimizeType,
      } = stocks[0]

      if (!companyId || !centerId || !itemCode || !orgItemCode) {
        return
      }

      // Base rows in simulator must always use renewalNumber = 0.
      // Some items come with non-zero renewalNumber in API response, which breaks
      // rowid mapping (0_* assumptions) and then disables style/recalculation logic.
      const baseRenewalNumber = 0

      const parentData = {
        companyId,
        centerId,
        orgItemCode,
        itemCode,
        itemName: itemName ?? null,
        janCase: janCase ?? null,
        expectedRank: expectedRank ?? null,
        orderUnitTypeName: orderUnitTypeName ?? null,
        caseLot: caseLot ?? null,
        minOrderQuantity: minOrderQuantity ?? null,
        stockDaysSetting,
        safetyStockSetting,
        minStockQuantity,
        startDate,
        stopShip,
        stopArrival,
        renewalFlg,
        renewalNumber: baseRenewalNumber,
        weightCalc,
        optimizeType,
        isTotal: false,
      }

      // ↓↓↓↓Create a unique row ID in rowid
      const arrivalRow: RenewalSimulatorRow = {
        ...parentData,
        rowType: ROW_TYPE_ARRIVAL,
        rowid: `${baseRenewalNumber}_${ROW_TYPE_ARRIVAL.code}`,
      }

      const shipRow: RenewalSimulatorRow = {
        ...parentData,
        rowType: ROW_TYPE_SHIP,
        rowid: `${baseRenewalNumber}_${ROW_TYPE_SHIP.code}`,
      }
      const salesOrderRow: RenewalSimulatorRow = {
        ...parentData,
        rowType: ROW_TYPE_SALES_ORDER,
        rowid: `${baseRenewalNumber}_${ROW_TYPE_SALES_ORDER.code}`,
      }
      const stockRow: RenewalSimulatorRow = {
        ...parentData,
        rowType: ROW_TYPE_STOCK,
        rowid: `${baseRenewalNumber}_${ROW_TYPE_STOCK.code}`,
      }
      const stockDaysRow: RenewalSimulatorRow = {
        ...parentData,
        rowType: ROW_TYPE_STOCK_DAYS,
        rowid: `${baseRenewalNumber}_${ROW_TYPE_STOCK_DAYS.code}`,
      }

      const stockTotal: RenewalSimulatorRow = {
        ...parentData,
        rowType: ROW_TYPE_STOCK_FOLLOW_BY_PRODUCT,
        rowid: `${baseRenewalNumber}_${ROW_TYPE_STOCK_FOLLOW_BY_PRODUCT.code}`,
      }
      const stockTotalDays: RenewalSimulatorRow = {
        ...parentData,
        rowType: ROW_TYPE_STOCK_PRODUCT,
        rowid: `${baseRenewalNumber}_${ROW_TYPE_STOCK_PRODUCT.code}`,
      }

      const remarksRow: RenewalSimulatorRow = {
        ...parentData,
        itemCode: '',
        itemName: '備考',
        rowid: '0_remark',
        rowType: ROW_TYPE_REMARK,
      }

      dayList.forEach((day) => {
        if (day.datevalue === null) {
          return
        }

        const matchedData = stocks.find(
          (stock) => stock.recDate && day.datevalue && isSameDay(stock.recDate, day.datevalue)
        )

        if (day.dateno !== null) {
          if (matchedData && matchedData.recDate) {
            const commonColumn = {
              recDate: matchedData.recDate,
              orderDate: matchedData.orderDate,
              isCurrentUpdate: false,
              oldValue: null,
              enableData: false,
              updateDatetime: matchedData.updateDatetime,
              stockPlanUpdateDatetime: matchedData.stockPlanUpdateDatetime,
              // ↓↓↓↓Setting renewal numbers for each date
              renewalNumberDay: matchedData.renewalNumber,
              orderQuantityRecommend: matchedData.orderQuantityRecommend,
              shipQuantityScheduled: matchedData.shipQuantityScheduled,
              colName : day.colname,
            }
            arrivalRow[`col${day.dateno}`] = {
              ...commonColumn,
              value: matchedData.arrivalQuantity,
              enableUpdate: matchedData.enableUpdateArrival || false,
              isUpdate: matchedData.isUpdatedArrival || false,
              recType: day.rectype,
              fixFlg: matchedData.arrivalFixFlg || false,
              orderQuantityRec: matchedData.orderQuantityRec || null,
              isOrderConfirmed: matchedData.isOrderConfirmed || false,
              isUpdatedArrivalTmp: matchedData.isUpdatedArrivalTmp || false,
              isArrivalDay: matchedData.isArrivalDay || false,
              isOrderDay: matchedData.isOrderDay || false,
              isTodayOrderDay: today?.isOrderDay || false,
            }

            shipRow[`col${day.dateno}`] = {
              ...commonColumn,
              value: matchedData.shipQuantity,
              enableUpdate: matchedData.enableUpdateShip || false,
              isUpdate: matchedData.isUpdatedShip || false,
              recType: day.rectype,
              enableData: matchedData.enableShip || false,
            }

            salesOrderRow[`col${day.dateno}`] = {
              ...commonColumn,
              value: matchedData.salesorderQuantity,
              enableUpdate: false,
              isUpdate: false,
              recType: day.rectype,
            }

            stockRow[`col${day.dateno}`] = {
              ...commonColumn,
              value: matchedData.stockQuantity,
              minStockQuantity: matchedData.minStockQuantity ?? null,
              avgShipQuantityStockBase: matchedData.avgShipQuantityStockBase ?? null,
              stockQuantityRec: matchedData.stockQuantityRec ?? null,
              enableUpdate: false,
              isUpdate: false,
              recType: day.rectype,
            }
            stockDaysRow[`col${day.dateno}`] = {
              ...commonColumn,
              value: matchedData.stockDays,
              stockDaysSetting: matchedData.stockDaysSetting ?? null,
              shipdaysNextArrival: matchedData.shipdaysNextArrival ?? null,
              avgShipQuantityStockBase: matchedData.avgShipQuantityStockBase ?? null,
              enableUpdate: false,
              isUpdate: false,
              recType: day.rectype,
            }
            stockTotal[`col${day.dateno}`] = {
              ...commonColumn,
              value: matchedData.stockQuantity,
              enableUpdate: false,
              isUpdate: false,
              recType: day.rectype,
            }

            stockTotalDays[`col${day.dateno}`] = {
              ...commonColumn,
              value: matchedData.stockQuantity,
              enableUpdate: false,
              isUpdate: false,
              recType: day.rectype,
            }
            remarksRow[`col${day.dateno}`] = {
              ...commonColumn,
              value: matchedData?.remarks || '',
              enableUpdate: true,
              isUpdate: !!matchedData.remarks,
              isCurrentUpdate: false,
              enableData: true,
              recType: day.rectype,
            }
          } else {
            arrivalRow[`col${day.dateno}`] = null
            shipRow[`col${day.dateno}`] = null
            stockRow[`col${day.dateno}`] = null
            salesOrderRow[`col${day.dateno}`] = null
            stockDaysRow[`col${day.dateno}`] = null
            stockTotal[`col${day.dateno}`] = null
            stockTotalDays[`col${day.dateno}`] = null
            remarksRow[`col${day.dateno}`] = null
          }
        }
      })

      rows.push(arrivalRow)
      rows.push(shipRow)
      rows.push(salesOrderRow)
      rows.push(stockRow)
      rows.push(stockDaysRow)
      rows.push(stockTotal)
      rows.push(stockTotalDays)
      rows.push(remarksRow)

      // ForEach oldItem and newItem
      renewalList.forEach((renewal) => {
        const dataJson = renewal
        const renewalRow = {
          ...parentData,
          startDate: dataJson.startDate,
          itemCode: dataJson?.orgItemCode ?? null,
          itemName: dataJson?.itemName ?? null,
          janCase: janCase ?? null,
          renewalNumber: dataJson?.renewalNumber,
        }
        const arrivalRenewalRow: RenewalSimulatorRow = {
          ...renewalRow,
          rowType: ROW_TYPE_ARRIVAL,
          rowid: `${dataJson.renewalNumber}_${ROW_TYPE_ARRIVAL.code}`,
        }

        const shipRenewalRow: RenewalSimulatorRow = {
          ...renewalRow,
          rowType: ROW_TYPE_SHIP,
          rowid: `${dataJson.renewalNumber}_${ROW_TYPE_SHIP.code}`,
        }

        const stockRenewalItemRow: RenewalSimulatorRow = {
          ...renewalRow,
          rowType: ROW_TYPE_STOCK,
          rowid: `${dataJson.renewalNumber}_${ROW_TYPE_STOCK.code}`,
        }
        const stopExclusive: RenewalSimulatorRow = {
          ...renewalRow,
          rowType: ROW_TYPE_SHIP_EXCLUSIVE,
          rowid: `${dataJson.renewalNumber}_${ROW_TYPE_SHIP_EXCLUSIVE.code}`,
        }
        const quantityStopShipping: RenewalSimulatorRow = {
          ...renewalRow,
          rowType: ROW_TYPE_QUANTITY_STOP_SHIPPING,
          rowid: `${dataJson.renewalNumber}_${ROW_TYPE_QUANTITY_STOP_SHIPPING.code}`,
        }

        dayList.forEach((day: any) => {
          if (day.datevalue === null) {
            return
          }
          const matchedData = stocks.find(
            (stock) => stock.recDate && day.datevalue && isSameDay(stock.recDate, day.datevalue)
          )

          const matchedDataOldItem = stocks.find(
            (stock) =>
              stock.recDate && day.datevalue && isSameDay(stock.recDate, day.datevalue) && stock.renewalNumber === 1
          )

          const matchedDataNewItem = stocks.find(
            (stock) =>
              stock.recDate && day.datevalue && isSameDay(stock.recDate, day.datevalue) && stock.renewalNumber === 2
          )

          if (day.dateno !== null) {
            // Old Item
            if (matchedData && matchedData.recDate) {
              const commonColumn = {
                recDate: matchedData.recDate,
                orderDate: matchedData.orderDate,
                isCurrentUpdate: false,
                oldValue: null,
                enableData: false,
                renewalNumberDay: dataJson.renewalNumber,
              }

              if (dataJson.renewalNumber === 1) {
                stopExclusive[`col${day.dateno}`] = {
                  ...commonColumn,
                  value: matchedData?.olditemShipStopFlg ?? false,
                  enableUpdate: true,
                  isUpdate: false,
                  recType: day.rectype,
                }

                quantityStopShipping[`col${day.dateno}`] = {
                  ...commonColumn,
                  value: matchedData?.olditemShipStopFlg ? 0 : matchedData.stockQuantity,
                  enableUpdate: false,
                  isUpdate: false,
                  recType: day.rectype,
                }
                // 旧商品の在庫数のデフォルトにitem_codeの在庫数を設定
                //  リニューアル画面は、新商品の適用前から表示されるので、初日は必ず旧商品が表示されている
                //  そのため、初日の旧商品の在庫数は、ItemCode単位の在庫数と一致する
                stockRenewalItemRow[`col${day.dateno}`] = {
                  ...commonColumn,
                  value: matchedData?.olditemShipStopFlg ? 0 : matchedData.stockQuantity,
                  minStockQuantity: matchedData.minStockQuantity ?? null,
                  avgShipQuantityStockBase: matchedData.avgShipQuantityStockBase ?? null,
                  stockQuantityRec: matchedData.stockQuantityRec ?? null,
                  enableUpdate: false,
                  isUpdate: false,
                  recType: day.rectype,
                }
              } else {
                // 新商品のデフォルト在庫数は０
                stockRenewalItemRow[`col${day.dateno}`] = {
                  ...commonColumn,
                  value: 0,
                  minStockQuantity: matchedData.minStockQuantity ?? null,
                  avgShipQuantityStockBase: matchedData.avgShipQuantityStockBase ?? null,
                  stockQuantityRec: matchedData.stockQuantityRec ?? null,
                  enableUpdate: false,
                  isUpdate: false,
                  recType: day.rectype,
                }
              }
              // todo:以下で設定しているデフォルトが意味わからない件
              arrivalRenewalRow[`col${day.dateno}`] = {
                ...commonColumn,
                value:
                  dataJson.renewalNumber === 1
                    ? matchedDataOldItem?.arrivalQuantity
                    : matchedDataNewItem?.arrivalQuantity,
                enableUpdate: false,
                isUpdate: matchedData.isUpdatedArrival || false,
                recType: day.rectype,
                fixFlg: matchedData.arrivalFixFlg || false,
                orderQuantityRec: matchedData.orderQuantityRec || null,
                isOrderConfirmed: matchedData.isOrderConfirmed || false,
                isUpdatedArrivalTmp: matchedData.isUpdatedArrivalTmp || false,
              }

              shipRenewalRow[`col${day.dateno}`] = {
                ...commonColumn,
                value: matchedData?.shipQuantity,
                enableUpdate: false,
                isUpdate: matchedData.isUpdatedShip || false,
                recType: day.rectype,
                enableData: matchedData.enableShip || false,
              }

            } else {
              arrivalRenewalRow[`col${day.dateno}`] = null
              shipRenewalRow[`col${day.dateno}`] = null
              stockRenewalItemRow[`col${day.dateno}`] = null
              stopExclusive[`col${day.dateno}`] = null
              quantityStopShipping[`col${day.dateno}`] = null
            }
          }
        })

        rows.push(arrivalRenewalRow)
        rows.push(shipRenewalRow)
        rows.push(stockRenewalItemRow)
        if (dataJson.renewalNumber === 1) {
          rows.push(stopExclusive)
          rows.push(quantityStopShipping)
        }
      })
    })

    return rows
  }

  /**
   * セルの値を変更した際の処理
   *
   * 無限ループを避けるため、編集の可能性がある入出荷行のセルのみを対象とする
   * 編集にともない、在庫数・在庫日数の再計算を行う
   */

  const onCellValueChangedBase = useCallback(
    (params: CellValueChangedEvent<RenewalSimulatorRow>) => {
      const gridApi = gridRef.current?.api
      if (params.data?.rowid === '1_olditemShipStopFlg') {
        return
      }

      if (['0_arrival', '0_ship', '0_remark'].includes(params.data.rowid) && !params.data.isTotal) {
        const oldVal = params.oldValue?.value ?? '';
        const newVal = (params.newValue && typeof params.newValue === 'object') ? params.newValue.value : '';
        
        if (!gridApi) return

        if (!params.node.id || !params.colDef.field) return
        const { field } = params.colDef

        const rowsInGroup = params.node.parent?.childrenAfterGroup
        if (!rowsInGroup) return

        const thisRow = rowsInGroup.find((row) => row.id === params.node.id)
        const thisRowNode = gridApi.getRowNode(thisRow?.id || '')
        if (!thisRowNode) return

        // Prevent infinite loop: Skip only when fromDeleteKey and NOT processing delete (newVal !== '')
        if (params.newValue?.fromDeleteKey && newVal !== '') {
          return;
        }

        // DETECT DELETE KEY: Check if arrival/ship has empty value (delete key pressed)
        if (['0_arrival', '0_ship'].includes(params.data.rowid) && newVal === '') {
          
          const stockNode = gridApi.getRowNode('0_stock')
          if (!stockNode) {
            return;
          }

          let recommendedValue;
          if (params.data.rowid === '0_arrival') {
            recommendedValue = (stockNode.data?.[field as keyof RenewalSimulatorRow] as RenewalSimulatorCell)?.orderQuantityRecommend || 0;
          } else if (params.data.rowid === '0_ship') {
            recommendedValue = (stockNode.data?.[field as keyof RenewalSimulatorRow] as RenewalSimulatorCell)?.shipQuantityScheduled || 0;
          }

          const cellDataValue = params.oldValue;
          let resetValue = cellDataValue?.value || 0;
          let newIsUpdate = cellDataValue?.isUpdate || false;
          let fromDeleteKey = true;

          // Determine which case to apply based on current state
          if ((cellDataValue?.isUpdate || cellDataValue?.isUpdatedArrivalTmp) && cellDataValue.value !== recommendedValue) {
            // Case 1: Cell is in updated state and current value differs from recommended
            // → Reset to recommended value and set isUpdate = false
            resetValue = recommendedValue;
            newIsUpdate = false;
            fromDeleteKey = false;

          } else if (cellDataValue?.isUpdate && cellDataValue.value === 0) {
            // Case 1b: Cell is updated with value=0 (from previous delete)
            // → Reset to recommended value and set isUpdate = false  
            resetValue = recommendedValue;
            newIsUpdate = false;

          } else if (!cellDataValue?.isUpdate && cellDataValue?.value !== 0) {
            // Case 2: Cell is in normal state and current value is not 0
            // → Reset to 0 and set isUpdate = true to apply updated-cell style
            resetValue = 0;
            newIsUpdate = true; // Set to true to show as updated cell

          } else if (cellDataValue?.isUpdate && cellDataValue.value === recommendedValue) {
            // Case 3: Cell is updated but current value equals recommended
            // → Keep recommended value but set isUpdate = false
            resetValue = recommendedValue;
            newIsUpdate = false;

          } else {
            // Case 4: Fallback case - ensure cell always has a value (prevent empty cells)
            // This handles cases like: isUpdate=false, currentValue=0, recommendedValue=0
            resetValue = cellDataValue?.value !== undefined ? cellDataValue.value : (recommendedValue || 0);
            newIsUpdate = cellDataValue?.isUpdate || false;

          }

          // Set the reset value
          thisRowNode.setDataValue(field, {
            ...cellDataValue,
            value: resetValue,
            isCurrentUpdate: true,
            isUpdate: newIsUpdate,
            oldValue: null,
            fromDeleteKey,
          });

          commitSimulatorRecalcFromColumn(field, thisRowNode)
          return;
        }
        if (oldVal === newVal) {
          if (!params.newValue) {
            thisRowNode.setDataValue(field, { ...params.oldValue });
          }
          return;
        }
        if (!params.newValue || (params.newValue.value == null && !params.newValue.resetToRecommend)) {  
          thisRowNode.setDataValue(field, {
            ...params.oldValue, 
            value: params.data.rowid === '0_remark' ? '' : 0,                  
            isCurrentUpdate: true,
            resetToRecommend: (newVal === '' || oldVal === 0),
            oldValue: params.oldValue?.value ?? null,
            stockPlanUpdateDatetime: params.oldValue?.stockPlanUpdateDatetime,
          });
          if (['0_arrival', '0_ship'].includes(params.data.rowid)) {
            commitSimulatorRecalcFromColumn(field, thisRowNode)
          } else {
            scheduleRenewalSimulatorCellClassRefresh(gridApi, [thisRowNode])
          }
          return
        }

        if (['0_arrival', '0_ship'].includes(params.data.rowid)) {
          const stockNode = gridApi.getRowNode('0_stock')
          if (stockNode) {
            let recommendedValue = null

            if (params.data.rowid === '0_arrival') {
              recommendedValue = (stockNode.data?.[field as keyof RenewalSimulatorRow] as RenewalSimulatorCell)?.orderQuantityRecommend || 0
            } else if (params.data.rowid === '0_ship') {
              recommendedValue = (stockNode.data?.[field as keyof RenewalSimulatorRow] as RenewalSimulatorCell)?.shipQuantityScheduled || 0
            }

            const cellDataValue = params.data?.[field as keyof RenewalSimulatorRow] as RenewalSimulatorCell;
            const resetToRecommend = (cellDataValue?.resetToRecommend || params.newValue?.resetToRecommend) && (cellDataValue?.isUpdate || cellDataValue?.isUpdatedArrivalTmp);
            
            if (resetToRecommend) {
              thisRowNode.setDataValue(field, {
                ...params.newValue,
                value: recommendedValue,
                isUpdate: false,
                isUpdatedArrivalTmp: (params.data.rowid === '0_arrival') ? !(recommendedValue === newVal) : params.newValue.isUpdatedArrivalTmp,
                isArrivalEdit: (params.data.rowid === '0_arrival'),
              });
              commitSimulatorRecalcFromColumn(field, thisRowNode)
              return;
            }

            // NEW CASE: Check if current value equals recommended but isUpdate = true
            // → Set isUpdate = false to match the state with the value
            if (newVal === recommendedValue && params.newValue?.isUpdate === true) {

              thisRowNode.setDataValue(field, {
                ...params.newValue,
                isUpdate: false,
                resetToRecommend: true,
                isCurrentUpdate: true,
              });

              commitSimulatorRecalcFromColumn(field, thisRowNode)
              return;
            }

            const isUpdateValue = newVal !== recommendedValue;

            const updatedCellValue = {
              ...params.newValue,
              isUpdate: isUpdateValue,
              resetToRecommend: !isUpdateValue,
              value: isUpdateValue ? newVal : recommendedValue,
              isArrivalEdit: (params.data.rowid === '0_arrival'),
            };
            
            if (resetToRecommend && 
                params.oldValue !== null && 
                recommendedValue !== null && 
                params.newValue.value !== recommendedValue) {
              updatedCellValue.resetToRecommend = false;
            }
            
         thisRowNode.setDataValue(field, updatedCellValue);
          }
        }

        if (params.data.rowid === '0_remark') {
          if (typeof params.newValue.value === 'string' || params.newValue.value === null) {
            const newRemark =
              params.newValue.value === null || params.newValue.value === undefined
                ? ''
                : String(params.newValue.value)
            const oldRemark = String(params.oldValue?.value ?? '')
            const hasRemarkChanged = newRemark !== oldRemark
            thisRowNode.setDataValue(field, {
              ...params.oldValue,
              value: params.newValue.value,
              isCurrentUpdate: true,
              isUpdate: hasRemarkChanged && (!!newRemark || !!oldRemark),
            })
          }
          scheduleRenewalSimulatorCellClassRefresh(gridApi, [thisRowNode])
          return
        }

        if (['0_arrival', '0_ship'].includes(params.data.rowid)) {
          commitSimulatorRecalcFromColumn(field, thisRowNode)
        }
      }
      
    },
    [commitSimulatorRecalcFromColumn]
  )
  /**
   * 編集されたセルのデータ一覧を取得する
   * @returns
   */
  function getEditedCells(): StockPlanUpdateBase[] {
    const gridApi = gridRef.current?.api
    if (!gridApi) return []

    const rowNodes: IRowNode<RenewalSimulatorRow>[] = []
    gridApi.forEachNode((node) => {
      if (
        node &&
        ['arrival', 'ship', 'remark'].includes(node.data?.rowType?.code || '') &&
        (node.data?.renewalNumber == null || node.data?.renewalNumber === 0)
      ) {
        rowNodes.push(node)
      }
      if (
        node &&
        ['olditemShipStopFlg'].includes(node.data?.rowType?.code || '') &&
        (node.data?.renewalNumber == null || node.data?.renewalNumber === 1)
      ) {
        rowNodes.push(node)
      }
    })

    const dataList: StockPlanUpdateBase[] = []
    const allCols = allNextCols(firstColName())

    // 日付カラムの全てのセル情報を取得
    const allCells = rowNodes
      .filter((node) => !!node.data)
      .map((node) => {
        return allCols.map((col) => {
          const cellData = node.data![col as keyof RenewalSimulatorRow] as RenewalSimulatorCell

          return {
            ...cellData,
            companyId: node.data!.companyId,
            centerId: node.data!.centerId,
            itemCode: node.data!.itemCode,
            rowTypeCode: node.data?.rowType?.code,
            // Ensure recDate is carried forward
          }
        })
      })
      .flat()

    // 今回変更された内容をセルから取得し、リクエスト用のデータに変換する（セル単位からレコード単位に変換）
    const groupedData = allCells.reduce(
      (acc, cellData) => {
        if (cellData) {
          const key = `${cellData.recDate}_${cellData.orderDate}`
          if (!acc[key]) {
            acc[key] = []
          }
          acc[key].push(cellData)
        }
        return acc
      },
      {} as Record<string, any[]>
    )

    Object.values(groupedData).forEach((cellGroup) => {
      const baseData = {
        companyId: cellGroup[0].companyId,
        centerId: cellGroup[0].centerId,
        itemCode: itemCodeProps,
        recDate: cellGroup[0].recDate,
        orderDate: cellGroup[0].orderDate,
        shipExclusionFlg: null,
        stockPlanUpdateDatetime: cellGroup[0].stockPlanUpdateDatetime ? convertDateToRequestParam(new Date(cellGroup[0].stockPlanUpdateDatetime)) : null,
        isUpdatedArrivalTmp: (cellGroup[0] as any).isArrivalEdit ?? false,
      }

      const existedData = dataList.find(
        (d) =>
          d.companyId === baseData.companyId &&
          d.centerId === baseData.centerId &&
          d.itemCode === baseData.itemCode &&
          d.recDate && baseData.recDate &&
          isSameDay(d.recDate, baseData.recDate)
      )

      if (existedData) {
        cellGroup.forEach((cellData) => {
          if (cellData.isCurrentUpdate) {
            switch (cellData.rowTypeCode) {
              case 'arrival':
                existedData.arrivalQuantityPlan = (!cellData.isUpdate || cellData.resetToRecommend) ? null : cellData.value;
                existedData.isUpdatedArrivalTmp = (cellData as any).isArrivalEdit ?? false;
                break
              case 'ship':
                existedData.shipQuantityPlan = (!cellData.isUpdate || cellData.resetToRecommend) ? null : cellData.value;
                
                break
              case 'remark':
                existedData.remarks = cellData.value
                break
              case 'olditemShipStopFlg':
                existedData.olditemShipStopFlg = cellData.value
                break
              default:
                break
            }
          }
        })
      } else {
        const editedCells = cellGroup.filter(c => c.isCurrentUpdate);

        if (editedCells.length > 0) {
          const newData = {
            ...baseData,
            remarks: cellGroup.find(c => c.rowTypeCode === 'remark')?.value || '',
            arrivalQuantityPlan: (() => {
              const cell = editedCells.find(c => c.rowTypeCode === 'arrival');
              if (!cell) return undefined;
              if (cell.orderQuantityRecommend === cell.value) return null;
              return cell.value;
            })(),
            shipQuantityPlan: (() => {
              const cell = editedCells.find(c => c.rowTypeCode === 'ship');
              if (!cell) return undefined;
              if (cell.shipQuantityScheduled === cell.value) return null;
              return cell.value;
            })(),
            olditemShipStopFlg: cellGroup.find(c => c.rowTypeCode === 'olditemShipStopFlg')?.value,
            isUpdatedArrivalTmp: baseData.isUpdatedArrivalTmp,
          }

          if (Object.values(newData).some(value => value !== undefined)) {
            dataList.push(newData)
          }
        }
      }
    })

    const findSameRecord = (sub: StockPlanUpdateBase, rowTypeCode: string, hasUpdateFlg: boolean) => {
      return allCells.find((c) => {
        const isSameDate =
          c.recDate && sub.recDate ? isSameDay(c.recDate, sub.recDate) : false;
    
        return (
          c.companyId === sub.companyId &&
          c.centerId === sub.centerId &&
          c.itemCode === sub.itemCode &&
          isSameDate &&
          c.rowTypeCode === rowTypeCode &&
          (hasUpdateFlg ? c.isUpdate : true)
        );
      });
    }

    // リクエスト内にすでに更新された内容が含まれない場合その内容が失われてしまうため、
    // 既存の更新内容が存在すればそれをセットする
    const requestDataList = dataList.map((dt) => {
      const d = { ...dt }
      if (d.arrivalQuantityPlan === undefined) {
        const updatedCell = findSameRecord(d, 'arrival', true)
        if (updatedCell) {
          // Check isUpdate/resetToRecommend before adding the value
          d.arrivalQuantityPlan = (!updatedCell.isUpdate || updatedCell.resetToRecommend) ? null : updatedCell.value;

        } else {
          const currentCell = findSameRecord(d, 'arrival', false)
          if (currentCell) {
            // Check isUpdate/resetToRecommend for current cell as well
            d.arrivalQuantityPlan = (!currentCell.isUpdate || currentCell.resetToRecommend) ? null : currentCell.value;
            
          } else {
            // セルが見つからない場合はnullを設定
            d.arrivalQuantityPlan = null
          }
        }
      }

      if (d.shipQuantityPlan === undefined) {
        const updatedCell = findSameRecord(d, 'ship', true)
        if (updatedCell) {
          d.shipQuantityPlan = (!updatedCell.isUpdate || updatedCell.resetToRecommend) ? null : updatedCell.value;
          
        } else {
          const currentCell = findSameRecord(d, 'ship', false)
          if (currentCell) {
            d.shipQuantityPlan = (!currentCell.isUpdate || currentCell.resetToRecommend) ? null : currentCell.value;
            
          } else {
            d.shipQuantityPlan = null
          }
        }
      }

      if (d.remarks === undefined) {
        const updatedCell = findSameRecord(d, 'remark', true)
        if (updatedCell) {
          d.remarks = updatedCell.value || '';
        
        }
      }

      if (d.olditemShipStopFlg === undefined) {
        const updatedCell = findSameRecord(d, 'olditemShipStopFlg', true)
        if (updatedCell) {
          d.olditemShipStopFlg = updatedCell.value
          // d.recDate = updatedCell.recDate
          // d.orderDate = updatedCell.orderDate
        }
      }
      return d
    })
    // 自動的にUTC認識されて日付がずれるため、共用関数convertDateToRequestParamで調整する。
    return requestDataList.map((d) => ({
      ...d,
      recDate: d.recDate ? convertDateToRequestParam(d.recDate) : d.recDate,
      orderDate: d.orderDate ? convertDateToRequestParam(d.orderDate) : d.orderDate,
    }))
  }
  const updateRenewalSimulator = async () => {
    const datalist = getEditedCells()
    if (datalist.length === 0) {
      if (onDialogCloseOnly) {
        onDialogCloseOnly()
        return
      }
      setUpdateAccept(false)
      return
    }

    const editItemcodeList = datalist
      .filter((e) => e.companyId && e.centerId && e.itemCode && e.recDate)
      .map((e) => e.itemCode!)
    const stockListRaw = stockData?.list ?? []
    const mergedList = stockListRaw.map((stock) => {
      const edit = datalist.find(
        (e) =>
          e.companyId === stock.companyId &&
          e.centerId === stock.centerId &&
          e.itemCode === stock.itemCode &&
          e.recDate &&
          stock.recDate &&
          isSameDay(e.recDate, stock.recDate)
      )
      if (edit) {
        return { 
          ...stock,
          arrivalQuantityPlan: edit.arrivalQuantityPlan ? Number(edit.arrivalQuantityPlan) : null,
          shipQuantityPlan: edit.shipQuantityPlan ? Number(edit.shipQuantityPlan) : null,
          remarks: edit.remarks ?? null
         }
      }
      return stock
    })
    const calculated = handleStockCalculation(
      stockListRaw,
      mergedList,
      loginUserInfo,
      {
        calculateShipQuantityStockBase: calcStockHook.calculateShipQuantityStockBase,
        calculateStockQuantity: calcStockHook.calculateStockQuantity,
        calculateAvgShipQuantityStockBase: calcStockHook.calculateAvgShipQuantityStockBase,
        calculateStockDays: calcStockHook.calculateStockDays,
      },
      calcParams,
      false,
      true,
      { centerSearch },
    )
    const stockListUpdate = calculated
      .filter((item) =>
        item.companyId &&
        item.centerId &&
        item.itemCode &&
        item.orgSupplierId &&
        item.recDate &&
        normalizeDate(item.recDate)! >= normalizeDate(systemDate)! &&
        editItemcodeList.includes(item.itemCode)
      )
      .map((item) => ({
        companyId: item.companyId ?? 0,
        centerId: item.centerId ?? 0,
        itemCode: item.itemCode ?? '',
        orgSupplierId: item.orgSupplierId,
        recDate: item.recDate ? convertDateToRequestParam(normalizeDate(item.recDate)!) : null,
        stockQuantity: item.stockQuantity,
        stockDays: item.stockDays,
        orderQuantityRecommend: item.orderQuantityRecommend,
        orderRecommendCalcDatetime: item.orderRecommendCalcDatetime ? convertDateToRequestParam(normalizeDate(item.orderRecommendCalcDatetime)!) : null,
        orderQuantityLpResult: item.orderQuantityLpResult,
        minStockForecastBased: item.minStockForecastBased,
        minStockShortageBased: item.minStockShortageBased,
        calcSafety: item.calcSafety,
        shipQuantityStockBase: item.shipQuantityStockBase,
        avgShipQuantityStockBase: item.avgShipQuantityStockBase,
        stockQuantityRecMorning: item.stockQuantityRecMorning,
      }))

    try {
      const response = await fetchUpdateStock({
        stockPlanUpdate: {
          body: datalist,
          stockList: stockListUpdate,
        },
      })

      if (response.result.status === 'error') {
        setErrorMessage(response.result)
        setUpdateAccept(false)
        if (setResultMessage) {
          setResultMessage(response.result)
        }
        return
      }  
      if (setResultMessage) {
        setResultMessage(response.result)
      }
      if (onDialogClose) {
        onDialogClose()
        setUpdateAccept(false)
      }
    } catch (e: any) {
      const errorResult: ResponseResult = {
        status: 'error',
        message: '在庫データの更新でエラーが発生しました。',
        pageNo: 0,
        systemDate: null,
        systemInfo: null,
      }
      setErrorMessage(errorResult)
      if (setResultMessage && e.result) {
        setResultMessage(e.result)
      }
      setUpdateAccept(false)
    }
  }

  useEffect(() => {
    if (updateAccept) {
      updateRenewalSimulator()
    }
  }, [updateAccept])

  /**
   * グリッドにデータが格納され準備ができた状態で呼び出すイベント
   * 以下の処理を行う
   *
   * - 在庫の計算
   * - 在庫日数の計算
   *
   * @returns
   */
  useEffect(() => {
    if (rowData.length > 0) {
      runInitialSimulatorGridCalc()
    }
  }, [rowData, runInitialSimulatorGridCalc])

  // using effect prevent jumping to today Column after edit
  useEffect(() => {
    const gridApi = gridRef.current?.api
    if (hasRunRef.current) return
    if (!gridApi || !rowData?.length) return

    gridApi.forEachNode((node) => {
      allNextCols(firstColName()).forEach((col) => {
        const dt = rowData[0]
        const dtNode = dt?.[col as keyof RenewalSimulatorRow] as RenewalSimulatorCell
        if (col && node && dtNode.recType === 'today') {
          gridApi.ensureColumnVisible(col, 'middle')
        }
      })
    })

    hasRunRef.current = true
  }, [rowData])


  /**
   * 再検索等でデータが更新されたタイミングで呼び出すイベント
   * 以下の処理を行う
   *
   * - 日付リストから絡む定義を作成
   * - グリッド用のデータ加工
   *
   */
  useEffect(() => {
    setDaylist(stockData.daylist)
    setRowData(resultToRows(stockData.list, stockData.daylist, stockData.renewalOrgItemList!))
  }, [stockData])

  useEffect(() => {
    const gridApi = gridRef.current?.api
    if (!gridApi || gridApi.isDestroyed()) return
  
    // manage loading by grid option (v34)
    gridApi.setGridOption('loading', !!(isLoading || isUpdating))
  }, [isLoading, isUpdating])

  shipStopToggleRef.current = (node, field, nextBool, baselineCell) => {
    const gridApi = gridRef.current?.api
    if (!gridApi || !field) return

    const refreshFlagRow = (): IRowNode<RenewalSimulatorRow> | undefined =>
      gridApi.getRowNode('1_olditemShipStopFlg') ?? node

    const runCalcAfterCommit = () => {
      queueMicrotask(() => {
        commitSimulatorRecalcFromColumn(firstColName())
      })
    }

    if (nextBool === false) {
      const flagNode = refreshFlagRow()
      if (!flagNode) return
      flagNode.setDataValue(field, {
        ...(baselineCell || {}),
        value: false,
        enableData: true,
        isCurrentUpdate: true,
      })
      runCalcAfterCommit()
      return
    }

    if (nextBool === true) {
      const allColumns = gridApi.getColumns()
      if (!allColumns) return

      allColumns.forEach((column) => {
        const colId = column.getColId()
        if (!allDayHeaders.includes(colId)) return
        const flagNode = refreshFlagRow()
        if (!flagNode?.data) return
        const cellData = flagNode.data[colId as keyof RenewalSimulatorRow] as RenewalSimulatorCell

        if (colId !== field && cellData?.value === true) {
          flagNode.setDataValue(colId, {
            ...((flagNode.data[colId as keyof RenewalSimulatorRow] as object) || {}),
            value: false,
            isCurrentUpdate: true,
          })
        }
      })

      const flagNodeFinal = refreshFlagRow()
      if (!flagNodeFinal) return
      flagNodeFinal.setDataValue(field, {
        ...(baselineCell || {}),
        value: true,
        recDate: baselineCell?.recDate,
        orderDate: baselineCell?.orderDate,
        enableData: true,
        isCurrentUpdate: true,
      })

      const afterUpdate = refreshFlagRow()
      if (afterUpdate?.data) {
        gridApi.applyTransaction({
          update: [afterUpdate.data],
        })
      }

      runCalcAfterCommit()
    }
  }

  return (
    <div style={containerStyle}>
      <div style={{ height: '100%', boxSizing: 'border-box' }}>
        <div style={gridStyle} className="ag-theme-balham">
          <AgGridReact
            ref={gridRef}
            components={{ OldItemShipStopFlgCellRenderer }}
            defaultColDef={defaultColDef}
            getRowId={(params) => params.data.rowid}
            columnDefs={columnDefs}
            localeText={localeText}
            rowData={rowData}
            getRowClass={getRowClass}
            groupDisplayType="custom"
            groupDefaultExpanded={1}
            suppressAggFuncInHeader
            rowHeight={36}
            onCellValueChanged={onCellValueChangedBase}
            loadingOverlayComponent={LoadingOverlay}
            cellSelection={{ enableHeaderHighlight: true, handle: { mode: 'fill', direction: 'xy' } }}
            rowNumbers
          />
        </div>
        <Grid direction="row" container justifyContent="space-between" width="75%" paddingTop={1.5}>
          <MessageInfo result={errorMessage} />
        </Grid>
      </div>
    </div>
  )
}

export default ItemRenewalSimulator
